import { colorStrings, mediaQueriesBP } from "@iewccouk/Utilities/variables";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import isMobile from "ismobilejs";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface SlideModel {
    fields: any;
    [fields.numberOfBrandsToDisplay]: string;
    [fields.brandsSelected]: any[];
    [fields.maximumNumberOfBrandsPerRow]: string;
    [fields.maximumBrandImageHeight]: number;
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    customCSS = "customCSS",
    // VERNDALE CUSTOM FEATURE
    title = "title",
    titleAlignment = "titleAlignment",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        [fields.title]: string; // VERNDALE CUSTOM FEATURE
        [fields.titleAlignment]: "left" | "center" | "right";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: any[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
    };
    extendedStyles?: SlideshowStyles;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandParameters = props.fields.slides.map((slide, idx) => ({
        // brandIds: slide.fields.brandsToDisplay === "random" ? [] : slide.fields.brandsSelected,
        // Now the brand returns selected brand info (id, name, logoPath, url)
        brandIds:
            slide.fields.brandsToDisplay === "random"
                ? []
                : slide.fields.brandsSelected.map((item: any) => item.fields?.brand?.id),
        randomize: slide.fields.brandsToDisplay === "random",
        pageSize: parseInt(slide.fields.numberOfBrandsToDisplay || "12", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
        idx,
        slide,
    }));

    return {
        state,
        brandParameters,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    titleText?: TypographyProps;
    imageItem?: FlexItemProps;
    imageContainer?: FlexWrapContainerProps;
}

export const slideshowStyles: SlideshowStyles = {
    titleText: {
        as: "h2",
        variant: "h2",
        css: css`
            margin-bottom: 24px;

            ${mediaQueriesBP.maxMD} {
                margin-bottom: 8px;
            }
        `,
    },
    slideshowWrapper: {
        css: css`
            padding: 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            top: 50%;
            border-radius: 50%;
            height: 40px;
            width: 40px;
            margin: 0;
            z-index: 1;
            padding: 0;
            color: transparent;
            left: -20px;
            transform: translateY(-50%) translateX(12px);
            display: flex;
            justify-content: center;
            align-items: center;

            ${mediaQueriesBP.maxMD} {
                left: -5px;
            }
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            span {
                top: unset;
                pointer-events: none;
            }
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }

            svg {
                color: ${getColor(colorStrings.primaryMain)};
                pointer-events: none;
            }
        `,
    },
    iconProps: {
        size: 24,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            border: 1px solid ${getColor("common.border")};
            min-height: 258px;
            background: ${getColor("common.global.white")};
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
            min-height: 258px;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
            background-color: ${getColor("common.global.white")};
            padding: 16px;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -20px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 10px;
            width: 10px;
            padding: 0;
            border-radius: 50%;
            margin: 0 4px;
            min-height: unset;
            border: 1px solid ${getColor("common.border")};
            background: ${getColor("common.white")};
            &:hover,
            &:active {
                background: ${getColor(colorStrings.primaryMain)};
                border: 1px solid ${getColor(colorStrings.primaryMain)};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            top: 50%;
            border-radius: 50%;
            height: 40px;
            width: 40px;
            margin: 0;
            z-index: 1;
            padding: 0;
            color: transparent;
            right: -20px;
            transform: translateY(-50%) translateX(-12px);
            display: flex;
            justify-content: center;
            align-items: center;

            ${mediaQueriesBP.maxMD} {
                right: -5px;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            span {
                top: unset;
                pointer-events: none;
            }
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }

            svg {
                pointer-events: none;
                color: ${getColor(colorStrings.primaryMain)};
            }
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
        `,
    },
    imageItem: {
        css: css`
            padding: 10px;
            justify-content: center;
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

const BrandCarousel: React.FC<Props> = props => {
    const { isInShell } = useShellContext();
    const history = useHistory();

    const styles = useMergeStyles("slideshow", slideshowStyles, props.extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, props.fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, props.fields.slides]);

    useEffect(() => {
        const { loadBrandGallery, brandParameters } = props;
        brandParameters.forEach(brandParameter => {
            loadBrandGallery({ ...brandParameter });
        });
    }, [props.loadBrandGallery]);

    const { fields, brandParameters } = props;

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const titleAlignStyles = {
        ...slideshowStyles.titleText,
        css: css`
            ${slideshowStyles.titleText?.css || ""}
            text-align: ${fields.titleAlignment};
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const showMobile = isMobile().any;

    const renderBrands = (
        brand: {
            fields: {
                brand: {
                    id: React.Key | undefined;
                    detailPagePath: string | undefined;
                    logoSmallImagePath: string | undefined;
                    name: any;
                    urlSegment: string | undefined;
                };
                destination: {
                    value: string | undefined;
                    type: string | undefined;
                };
                openInNewTab: boolean;
            };
        },
        flexColumns: any,
        imgProps: any,
    ) => {
        if (!brand.fields.brand) {
            return null;
        }

        const destination = brand.fields.destination.value
            ? brand.fields.destination.value
            : `/brands/${brand.fields.brand?.urlSegment ?? ""}`;

        // Verndale Customization
        const ga4ClassName = `verndaleGA4__brand-logo`;
        // End Verndale Customization

        return (
            <FlexItem key={brand.fields.brand.id} flexColumns={flexColumns} {...styles.imageItem}>
                <Clickable
                    target={brand.fields.openInNewTab ? "_blank" : "_self"}
                    href={destination}
                    {...styles.link}
                    className={ga4ClassName}
                >
                    <LazyImage
                        imgProps={imgProps}
                        {...styles.image}
                        src={brand.fields.brand.logoSmallImagePath}
                        altText={`${translate("Go to brand page")} ${brand.fields.brand.name}`}
                        className="brand-logo"
                    />
                </Clickable>
            </FlexItem>
        );
    };

    const renderBrandParametersOnDesktop = () => {
        return brandParameters.map((parameter, index) => {
            const id: string = uniqueId();

            const slideWrapperStyles = {
                css: css`
                    ${styles.slideContentWrapper?.css || ""}
                `,
            };

            const desktop = parseInt(parameter.slide.fields.maximumNumberOfBrandsPerRow, 10);
            const tablet = Math.ceil(desktop / 2);
            const mobile = Math.ceil(tablet / 2);

            const imgProps = {
                style: {
                    width: "auto",
                    maxWidth: "100%",
                    maxHeight: `${parameter.slide.fields.maximumBrandImageHeight}px`,
                },
            };

            return (
                <StyledWrapper className="slide-wrapper" key={id} {...slideWrapperStyles}>
                    <FlexWrapContainer {...styles.imageContainer}>
                        {parameter.slide.fields.brandsSelected &&
                            parameter.slide.fields.brandsSelected.map((brand: any) =>
                                renderBrands(brand, [mobile, tablet, tablet, desktop, desktop], imgProps),
                            )}
                    </FlexWrapContainer>
                </StyledWrapper>
            );
        });
    };

    const buildSlides = () => {
        const allFields: any[] = [];
        brandParameters.forEach(parameter => {
            parameter.slide.fields.brandsSelected.forEach((brand: any) => {
                allFields.push(brand);
            });
        });

        const fieldsInPairs: any[] = [];
        let tempFields: any = [];
        allFields.forEach((brand, idx) => {
            tempFields.push(brand);
            if ((idx + 1) % 2 === 0) {
                fieldsInPairs.push(tempFields);
                tempFields = [];
            }
        });

        return fieldsInPairs;
    };

    const renderBrandParametersOnMobile = () => {
        const fieldsInPairs = buildSlides();
        return fieldsInPairs.map((slideWrapper, index) => {
            const id: string = uniqueId();

            const slideWrapperStyles = {
                css: css`
                    ${styles.slideContentWrapper?.css || ""}
                `,
            };

            const imgProps = {
                style: {
                    width: "auto",
                    maxWidth: "100%",
                    maxHeight: "100px",
                },
            };

            return (
                <StyledWrapper className="slide-wrapper" key={id} {...slideWrapperStyles}>
                    <FlexWrapContainer {...styles.imageContainer}>
                        {slideWrapper.map((brand: any) => renderBrands(brand, 1, imgProps))}
                    </FlexWrapContainer>
                </StyledWrapper>
            );
        });
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            {fields.title && (
                <Typography {...titleAlignStyles} data-test-selector="brandCarouselTitle">
                    {fields.title}
                </Typography>
            )}

            {fields.slides.length > 0 && (
                <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                    {fields.showArrows && fields.slides.length > 1 && (
                        <StyledWrapper {...styles.prevArrowWrapper}>
                            <Button
                                className="verndaleGA4__slider--btn"
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                            >
                                <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                            {!showMobile && renderBrandParametersOnDesktop()}
                            {showMobile && renderBrandParametersOnMobile()}
                        </StyledWrapper>
                    </StyledWrapper>

                    {fields.slideIndicator && (
                        <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                            {(showMobile ? buildSlides() : fields.slides).map((_, i) => {
                                const id: string = uniqueId();

                                const dotColorStyles =
                                    i === selectedIndex
                                        ? `
                                        background: white;
                                        &:hover { background: white; }
                                    `
                                        : "";
                                const dotButtonStyles = {
                                    css: css`
                                        ${styles.dotButton?.css || ""}
                                        ${dotColorStyles}
                                    `,
                                };

                                return (
                                    <Button
                                        className="slide-dot-button"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={id}
                                        {...dotButtonStyles}
                                        onClick={() => embla && embla.scrollTo(i)}
                                    />
                                );
                            })}
                        </StyledWrapper>
                    )}
                    {fields.showArrows && fields.slides.length > 1 && (
                        <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                            <Button
                                className="slide-arrow verndaleGA4__slider--btn"
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                            >
                                <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                            </Button>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandCarousel as any) as any,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            // VERNDALE CUSTOM FEATURE
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: contentTab,
            },
            {
                name: fields.titleAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                tab: contentTab,
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },

            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: fields.numberOfBrandsToDisplay,
                        displayName: "Number of brands to display",
                        editorTemplate: "DropDownField",
                        defaultValue: "12",
                        options: [
                            { displayName: "1", value: "1" },
                            { displayName: "2", value: "2" },
                            { displayName: "3", value: "3" },
                            { displayName: "4", value: "4" },
                            { displayName: "5", value: "5" },
                            { displayName: "6", value: "6" },
                            { displayName: "7", value: "7" },
                            { displayName: "8", value: "8" },
                            { displayName: "9", value: "9" },
                            { displayName: "10", value: "10" },
                            { displayName: "11", value: "11" },
                            { displayName: "12", value: "12" },
                        ],

                        isVisible: item => item.fields.brandsToDisplay === "random",
                    },
                    // {
                    //     name: fields.brandsSelected,
                    //     displayName: "Select brands",
                    //     editorTemplate: "CustomSelectBrandsField" as any,
                    //     isVisible: item => item.fields.brandsToDisplay === "select-brands",
                    //     defaultValue: [],
                    // },
                    {
                        name: fields.brandsSelected,
                        displayName: "Select Brands",
                        editorTemplate: "ListField",
                        getDisplay: item => item.fields.brand?.name ?? "",
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "brand",
                                displayName: "Brand",
                                editorTemplate: "CustomSelectBrandField" as any,
                                defaultValue: null,
                                isRequired: true,
                            },
                            {
                                name: "destination",
                                displayName: "External Link",
                                editorTemplate: "CustomExternalLinkField" as "LinkField",
                                defaultValue: { type: "Url", value: "" },
                                isRequired: false,
                                tooltip: "If provided, clicking on the brand will navigate to this URL.",
                            },
                            {
                                name: "openInNewTab",
                                displayName: "Open in a new tab",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                    {
                        name: fields.maximumNumberOfBrandsPerRow,
                        displayName: "Maximum brands per row",
                        editorTemplate: "DropDownField",
                        defaultValue: "6",
                        options: [
                            { displayName: "1", value: "1" },
                            { displayName: "2", value: "2" },
                            { displayName: "3", value: "3" },
                            { displayName: "4", value: "4" },
                            { displayName: "5", value: "5" },
                            { displayName: "6", value: "6" },
                        ],

                        tooltip:
                            "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
                        tooltipPosition: "left",
                    },
                    {
                        name: fields.maximumBrandImageHeight,
                        displayName: "Maximum brand image height",
                        editorTemplate: "IntegerField",
                        defaultValue: 100,

                        tooltip:
                            "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
